import { defaultBindings } from '~/store/keyBindings'

// A stored binding equal to a default since handed to another action; dropping it reverts that one deliberate choice too.
const retired = {
	show_param_column: 'O',
	tap_tempo: 'Shift+T',
	next_preset_instant: 'Ctrl+]',
	prev_preset_instant: 'Ctrl+[',
}

// Action names taken out of the registry whose stored key would still collide.
const retiredActions = new Set(['toggle_video'])

/**
 * Stored bindings win, then defaults fill in — but only onto a free key.
 *
 * ⚠️ A key taken from a default action (Settings or Learn moves it, deleting the old owner) is stored as the new owner holding it and the old one ABSENT. Spreading the defaults back over that restores the old owner on the same key: two actions on one key after every reload, or the new owner dropped when it has no default. So a default whose key a stored binding holds stays absent.
 *
 * A stored binding on an action with no default is kept unless its key is already held — the registry is out of reach here (it imports the store), so a dead name is told apart by collision: kept, it only occupies a key nobody else wants, and the next binding to that key takes it over.
 *
 * Still a default reappears when it was simply unbound and nothing took its key: absence cannot say "removed" apart from "added in a later release".
 */
export default function reconcileKeyBindings(persisted, current) {
	const stored = {}
	const extra = {}
	if (persisted?.bindings) {
		for (const key in persisted.bindings) {
			const hotkey = persisted.bindings[key]
			if (key in defaultBindings) {
				if (hotkey !== retired[key]) stored[key] = hotkey
			} else if (hotkey && !retiredActions.has(key)) extra[key] = hotkey
		}
	}
	const bindings = { ...stored }
	const taken = new Set(Object.values(bindings))
	const claim = (key, hotkey) => {
		if (!hotkey || taken.has(hotkey)) return
		bindings[key] = hotkey
		taken.add(hotkey)
	}
	for (const [key, hotkey] of Object.entries(extra)) claim(key, hotkey)
	for (const [key, hotkey] of Object.entries(defaultBindings)) if (!(key in bindings)) claim(key, hotkey)
	return {
		...current,
		...persisted,
		bindings,
	}
}
